import React from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE_LARGE, EMPTY_DETAIL } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { statusOptions } from '@constants/masterData';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime } from '@utils';
import { Button, Flex } from 'antd';

import CustomerForm from './CustomerForm';

const message = defineMessages({
    objectName: 'Customer',
});

const FORM_ID = 'form-customer';

const CustomerListPage = () => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(statusOptions, ['label']);

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.customer.getList,
            getById: apiConfig.customer.getById,
            create: apiConfig.customer.create,
            update: apiConfig.customer.update,
            delete: apiConfig.customer.delete,
            changeStatus: apiConfig.customer.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(commonMessage.customer),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.fullName),
            dataIndex: 'userName',
        },
        {
            title: translate.formatMessage(commonMessage.username),
            dataIndex: 'userUsername',
            width: '240px',
        },
        {
            title: translate.formatMessage(commonMessage.phone),
            dataIndex: 'userPhone',
            width: '200px',
        },
        {
            title: translate.formatMessage(commonMessage.email),
            dataIndex: 'userEmail',
            width: '200px',
        },
        {
            title: translate.formatMessage(commonMessage.createdDate),
            dataIndex: 'createdDate',
            render: (createdDate) => convertUtcToLocalTime(createdDate, DEFAULT_FORMAT.DATE, 'DD-MM-YYYY'),
            width: '150px',
        },

        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                changeStatus: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'username',
            placeholder: translate.formatMessage(commonMessage.username),
        },
        {
            key: 'fullName',
            placeholder: translate.formatMessage(commonMessage.fullName),
        },
        {
            key: 'email',
            placeholder: translate.formatMessage(commonMessage.email),
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.statusSelect),
            type: FieldTypes.SELECT,
            options: statusValues,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.customer) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        loading={loading}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <CustomerForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={onSave}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default CustomerListPage;
